import { privateApi } from './api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

/** [x, y, w, h] in raw (unrotated) jp2 pixels. */
export type FaunaBBox = [number, number, number, number];

// code is the Latin name, name the English label shown to users.
export type FaunaTaxon = {
    code: string;
    name: string;
    parent: string | null;
};

export type FaunaAnimal = {
    id: string;
    taxon: string;
    taxon_note: string | null;
    event: 'arrived' | 'left' | 'moved' | null;
    bbox: FaunaBBox;
    length_mm: number | null;
    depth_mm: number | null;
    origin: 'detected' | 'added';
    last_edit: { by: { id: string; name: string }; at: string } | null;
    thumbnailUrl: string | null;
    blinkUrl: string | null;
    // Boxed or added since the last publication: no thumbnail or overlay yet.
    pending: boolean;
};

/** One image's fauna, edits already replayed by the backend. */
export type ImageFauna = {
    date: string;
    source: string;
    image: { width: number; height: number; um_per_px: number };
    overlayUrl: string | null;
    taxa: FaunaTaxon[];
    // Already ordered sharpest first — display as is.
    animals: FaunaAnimal[];
};

// Presigned URLs live 10 minutes; refetch well before they expire.
const FAUNA_STALE_MS = 5 * 60 * 1000;

const getSampleFaunaImageIds = async (projectId: string, sampleId: string) => {
    const { data } = await privateApi.get<string[]>(
        `/project/${projectId}/fauna/sample/${sampleId}`,
    );
    return data;
};

const getImageFauna = async (projectId: string, imageId: string) => {
    const { data } = await privateApi.get<ImageFauna | null>(
        `/project/${projectId}/fauna/image/${imageId}`,
    );
    // Nest answers a null body as an empty string.
    return data || null;
};

/** Ids of the sample's images that have fauna data (empty when none / hidden). */
export const useSampleFaunaImageIds = (projectId?: string, sampleId?: string) => {
    return useQuery({
        queryKey: ['fauna', 'sample', projectId, sampleId],
        queryFn: () => {
            if (!projectId || !sampleId)
                return Promise.reject('Project ID or Sample ID missing');
            return getSampleFaunaImageIds(projectId, sampleId);
        },
        enabled: !!projectId && !!sampleId,
        staleTime: FAUNA_STALE_MS,
    });
};

export const useImageFauna = (
    projectId?: string,
    imageId?: string,
    enabled = true,
) => {
    return useQuery({
        queryKey: ['fauna', 'image', projectId, imageId],
        queryFn: () => {
            if (!projectId || !imageId)
                return Promise.reject('Project ID or Image ID missing');
            return getImageFauna(projectId, imageId);
        },
        enabled: enabled && !!projectId && !!imageId,
        staleTime: FAUNA_STALE_MS,
        gcTime: FAUNA_STALE_MS,
    });
};

/** The label shown for a taxon: its Latin code. */
export const taxonLabel = (code: string): string => code;

/** The English name of a taxon, when the image's taxa list has it. */
export const taxonName = (code: string, taxa: FaunaTaxon[]): string | undefined =>
    taxa.find((t) => t.code === code)?.name;

/** Whether the user may correct the fauna analysis of this project. */
export const useFaunaPermissions = (projectId?: string) => {
    return useQuery({
        queryKey: ['fauna', 'permissions', projectId],
        queryFn: async () => {
            if (!projectId) return Promise.reject('Project ID missing');
            const { data } = await privateApi.get<{ canEdit: boolean }>(
                `/project/${projectId}/fauna/permissions`,
            );
            return data;
        },
        enabled: !!projectId,
        staleTime: Infinity,
    });
};

/**
 * One correction as sent to the backend, which adds the author, the time and
 * the id of an added animal. Coordinates are raw (unrotated) jp2 pixels.
 */
export type FaunaChange =
    | { action: 'reject'; id: string; bbox: FaunaBBox }
    | {
          action: 'retaxon';
          id: string;
          bbox: FaunaBBox;
          taxon: string;
          taxon_note: string | null;
      }
    | { action: 'rebox'; id: string; bbox: FaunaBBox; new_bbox: FaunaBBox }
    | {
          action: 'add';
          bbox: FaunaBBox;
          taxon: string;
          taxon_note: string | null;
      };

/** Save corrections to one image: each call is one new edits/ file. */
export const useSaveFaunaEdit = (projectId?: string, imageId?: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (changes: FaunaChange[]) => {
            if (!projectId || !imageId)
                return Promise.reject('Project ID or Image ID missing');
            const { data } = await privateApi.post<{ file: string }>(
                `/project/${projectId}/fauna/image/${imageId}/edits`,
                { changes },
            );
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['fauna', 'image', projectId, imageId],
            });
        },
    });
};
